import { useEffect, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PeriodSelector } from '@/components/dashboard/performance/period-selector';
import {
  TopBarActions,
  TopBarDateRange,
  TopBarTitle,
} from '@/components/layout/top-bar-slots';
import { HeaderActions } from '@/components/strategy-detail/header/header-actions';
import { StrategyDetailPage } from '@/components/strategy-detail/strategy-detail-page';
import type { ForecastHorizon } from '@/components/strategy-detail/trend/forecast-series';
import type { TrendMetric } from '@/components/strategy-detail/trend/trend-formatters';
import { Skeleton } from '@/components/ui/skeleton';
import { useRecommendations } from '@/hooks/data/use-recommendations';
import {
  useStrategyCampaigns,
  useStrategyDetail,
  useStrategyTrend,
} from '@/hooks/data/use-strategies';
import {
  useStrategyForecast,
  useStrategyForecastAccuracy,
} from '@/hooks/data/use-strategy-forecast';
import { useSeedAutoMention } from '@/hooks/copilot/use-seed-auto-mention';
import type { DateRangeParams } from '@/lib/date-range';
import { MessageContextType } from '@/types/generated/enums';
import { loadNamespaces } from '@/lib/i18n/i18n';

const searchSchema = z.object({
  metric: z
    .enum(['ROAS', 'REVENUE', 'SPEND', 'CPA', 'CONVERSIONS'])
    .default('ROAS'),
  date_range: z
    .enum(['LAST_7D', 'LAST_14D', 'LAST_30D', 'LAST_90D', 'CUSTOM'])
    .default('LAST_14D'),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  forecast_horizon: z.coerce
    .number()
    .pipe(z.union([z.literal(7), z.literal(30), z.literal(90)]))
    .default(30)
    .catch(30),
  campaigns_page: z.coerce.number().int().min(1).default(1).catch(1),
});

const CAMPAIGNS_PAGE_LIMIT = 20;

export const Route = createFileRoute('/_authenticated/strategies/$strategyId/')(
  {
    loader: () => loadNamespaces('goals', 'strategy'),
    validateSearch: searchSchema,
    staticData: {
      title: 'route.strategy.title',
      parent: { to: '/strategies', title: 'route.strategies.title' },
      messageContext: { context_type: 'strategy', idParam: 'strategyId' },
    },
    component: StrategyDetailRoute,
  },
);

function StrategyDetailRoute(): React.ReactElement {
  const { t } = useTranslation('strategy');
  const { strategyId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const range: DateRangeParams = useMemo(
    () => ({
      date_range: search.date_range,
      start_date: search.start_date,
      end_date: search.end_date,
    }),
    [search.date_range, search.start_date, search.end_date],
  );

  const detail = useStrategyDetail(strategyId);
  const campaigns = useStrategyCampaigns(strategyId, {
    page: search.campaigns_page,
    limit: CAMPAIGNS_PAGE_LIMIT,
  });
  const trend = useStrategyTrend(strategyId, range);
  const forecast = useStrategyForecast(strategyId, search.forecast_horizon);
  const forecastAccuracy = useStrategyForecastAccuracy(strategyId);
  const recommendations = useRecommendations();

  // A page past the real last one otherwise renders as a false "no campaigns" with no pager back.
  const campaignsTotalPages = campaigns.data
    ? Math.max(1, Math.ceil(campaigns.data.total / CAMPAIGNS_PAGE_LIMIT))
    : 1;
  const campaignsPageOutOfRange =
    campaigns.data !== undefined && search.campaigns_page > campaignsTotalPages;

  useEffect(() => {
    if (!campaignsPageOutOfRange) return;
    void navigate({
      to: '/strategies/$strategyId',
      search: { ...search, campaigns_page: campaignsTotalPages },
      replace: true,
    });
  }, [campaignsPageOutOfRange, campaignsTotalPages, navigate, search]);

  useSeedAutoMention(
    detail.data
      ? {
          type: MessageContextType.STRATEGY,
          id: detail.data.id,
          label: detail.data.name,
        }
      : null,
  );

  const onMetricChange = (next: TrendMetric): void => {
    void navigate({
      to: '/strategies/$strategyId',
      search: { ...search, metric: next },
      replace: true,
    });
  };

  const onHorizonChange = (next: ForecastHorizon): void => {
    void navigate({
      to: '/strategies/$strategyId',
      search: { ...search, forecast_horizon: next },
      replace: true,
    });
  };

  const onPeriodChange = (next: DateRangeParams): void => {
    void navigate({
      to: '/strategies/$strategyId',
      search: {
        ...search,
        date_range: next.date_range,
        start_date: next.start_date,
        end_date: next.end_date,
      },
      replace: true,
    });
  };

  const onCampaignsPageChange = (next: number): void => {
    void navigate({
      to: '/strategies/$strategyId',
      search: { ...search, campaigns_page: next },
      replace: true,
    });
  };

  if (detail.isError || trend.isError) {
    return (
      <div className="mx-auto w-full max-w-285 px-4 py-12 sm:px-9">
        <p className="text-danger-700 text-fs-13">
          {t('strategy_detail_page.load_failed')}
        </p>
      </div>
    );
  }

  if (!detail.data || !trend.data) {
    return (
      <div className="mx-auto flex w-full max-w-285 flex-col gap-4 px-4 pt-5 pb-12 sm:px-9 sm:pt-7">
        <Skeleton variant="card" className="h-10 w-1/3" />
        <Skeleton variant="card" className="h-32 w-full" />
        <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @3xl:grid-cols-4">
          <Skeleton variant="card" className="h-24 w-full" />
          <Skeleton variant="card" className="h-24 w-full" />
          <Skeleton variant="card" className="h-24 w-full" />
          <Skeleton variant="card" className="h-24 w-full" />
        </div>
        <Skeleton variant="card" className="h-50 w-full" />
        <Skeleton variant="card" className="h-72 w-full" />
      </div>
    );
  }

  return (
    <>
      <TopBarTitle>{detail.data.name}</TopBarTitle>
      <TopBarDateRange>
        <PeriodSelector value={range} onChange={onPeriodChange} />
      </TopBarDateRange>
      <TopBarActions>
        <HeaderActions strategy={detail.data} range={range} />
      </TopBarActions>
      <StrategyDetailPage
        strategy={detail.data}
        campaigns={campaigns}
        campaignsPageOutOfRange={campaignsPageOutOfRange}
        campaignsPage={search.campaigns_page}
        campaignsLimit={CAMPAIGNS_PAGE_LIMIT}
        onCampaignsPageChange={onCampaignsPageChange}
        trend={trend.data}
        recommendations={recommendations.data ?? []}
        metric={search.metric}
        onMetricChange={onMetricChange}
        forecast={forecast.data ?? null}
        forecastAccuracy={forecastAccuracy.data ?? null}
        horizon={search.forecast_horizon}
        onHorizonChange={onHorizonChange}
        range={range}
      />
    </>
  );
}
